import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Autocomplete,
  Grid,
  MenuItem,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { ArrowBack, Save } from '@mui/icons-material';
import { PageHeader, headerCellSx } from '../../components/common/pageKit';
import { useControlRoomColors } from '../../components/common/controlRoom';
import { formatIndianNumber } from '../../utils/currency';
import { api } from '../../services/apiClient';
import { invoiceService, Invoice, InvoiceItem } from '../../services/invoiceService';
import { customerService, Customer } from '../../services/customerService';
import { salesReturnService, ReturnType, ItemCondition } from '../../services/salesReturnService';

// ---------------------------------------------------------------------------
// New Sales Return, as a dedicated full page mirroring SalesContractFormPage
// / QuotationFormPage: titled sections, a sticky bottom action bar, and a
// returnable-lines grid seeded from the selected invoice's own line items.
// The server is the source of truth for GST/totals and for capping quantity
// at invoiced-minus-already-returned; the totals shown here are informational.
// ---------------------------------------------------------------------------

const RETURN_TYPES: { value: ReturnType; label: string }[] = [
  { value: 'DAMAGED', label: 'Damaged' },
  { value: 'WRONG_ITEM', label: 'Wrong Item' },
  { value: 'EXCESS', label: 'Excess Delivered' },
  { value: 'QUALITY_ISSUE', label: 'Quality Issue' },
  { value: 'CUSTOMER_REJECTION', label: 'Customer Rejection' },
  { value: 'OTHER', label: 'Other' },
];

const CONDITIONS: ItemCondition[] = ['GOOD', 'DAMAGED', 'SCRAP'];

interface WarehouseOption {
  id: number | string;
  warehouseCode: string;
  warehouseName: string;
  isActive?: boolean | number;
}

let draftKeySeq = 0;
const nextDraftKey = () => `draft-${++draftKeySeq}`;

interface LineDraft {
  key: string;
  invoiceItem: InvoiceItem;
  returnQty: string;
  condition: ItemCondition;
  description: string;
}

const toNum = (v: string | number | null | undefined): number => {
  if (v === null || v === undefined || v === '') return 0;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : 0;
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const SalesReturnFormPage: React.FC = () => {
  const navigate = useNavigate();
  const c = useControlRoomColors();

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [warehouses, setWarehouses] = useState<WarehouseOption[]>([]);
  const [loadingRefData, setLoadingRefData] = useState(true);

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [customerInvoices, setCustomerInvoices] = useState<Invoice[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loadingInvoiceDetail, setLoadingInvoiceDetail] = useState(false);
  const [lines, setLines] = useState<LineDraft[]>([]);

  const [returnDate, setReturnDate] = useState(todayISO());
  const [returnType, setReturnType] = useState<ReturnType>('DAMAGED');
  const [reason, setReason] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [restock, setRestock] = useState(true);

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  useEffect(() => {
    setLoadingRefData(true);
    Promise.all([
      customerService.getCustomers().catch(() => [] as Customer[]),
      api.get<{ warehouses: WarehouseOption[] }>('/inventory/warehouses').catch(() => ({ warehouses: [] as WarehouseOption[] })),
    ])
      .then(([customersRes, warehousesRes]) => {
        setCustomers(customersRes || []);
        setWarehouses((warehousesRes.warehouses || []).filter((w) => w.isActive === true || Number(w.isActive) === 1));
      })
      .finally(() => setLoadingRefData(false));
  }, []);

  // Non-cancelled invoices for the selected customer.
  useEffect(() => {
    setInvoice(null);
    setLines([]);
    if (!customer) {
      setCustomerInvoices([]);
      return;
    }
    setLoadingInvoices(true);
    invoiceService
      .getInvoices({ customerId: Number(customer.id) })
      .then((invoices) => setCustomerInvoices(invoices.filter((inv) => inv.status !== 'CANCELLED')))
      .catch(() => setCustomerInvoices([]))
      .finally(() => setLoadingInvoices(false));
  }, [customer]);

  // Load the selected invoice's own line items to seed the returnable grid.
  useEffect(() => {
    if (!invoice) {
      setLines([]);
      return;
    }
    setLoadingInvoiceDetail(true);
    invoiceService
      .getInvoiceById(invoice.id)
      .then((full) => {
        setLines(
          (full.items || []).map((it) => ({
            key: nextDraftKey(),
            invoiceItem: it,
            returnQty: '0',
            condition: 'GOOD' as ItemCondition,
            description: '',
          }))
        );
      })
      .catch((err: any) => setError(err.message || 'Failed to load invoice items'))
      .finally(() => setLoadingInvoiceDetail(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice]);

  const updateLine = (key: string, patch: Partial<LineDraft>) =>
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const lineComputed = (l: LineDraft) => {
    const qty = toNum(l.returnQty);
    const taxable = qty * toNum(l.invoiceItem.unitPrice);
    const tax = (taxable * toNum(l.invoiceItem.gstRate)) / 100;
    return { taxable, tax, total: taxable + tax };
  };

  const totals = useMemo(() => {
    let subtotal = 0;
    let totalTax = 0;
    lines.forEach((l) => {
      const { taxable, tax } = lineComputed(l);
      subtotal += taxable;
      totalTax += tax;
    });
    return { subtotal, totalTax, total: subtotal + totalTax };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines]);

  const returnLinesCount = lines.filter((l) => toNum(l.returnQty) > 0).length;

  const validate = (): string[] => {
    const errs: string[] = [];
    if (!customer) errs.push('Select the customer this return is for.');
    if (!invoice) errs.push('Select the invoice being returned against.');
    if (!returnDate) errs.push('Return date is required.');
    if (returnLinesCount === 0) errs.push('Enter a return quantity greater than 0 for at least one line.');
    lines.forEach((l, idx) => {
      const qty = toNum(l.returnQty);
      const invoicedQty = toNum(l.invoiceItem.quantity);
      if (qty < 0) errs.push(`Line ${idx + 1}: return quantity cannot be negative.`);
      if (qty > invoicedQty) errs.push(`Line ${idx + 1}: return quantity cannot exceed the invoiced quantity (${invoicedQty}).`);
    });
    return errs;
  };

  const handleSubmit = async () => {
    const errs = validate();
    setValidationErrors(errs);
    if (errs.length > 0) return;

    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        invoiceId: invoice!.id,
        returnDate,
        returnType,
        reason: reason.trim() || undefined,
        warehouseId: warehouseId || undefined,
        restock,
        lines: lines
          .filter((l) => toNum(l.returnQty) > 0)
          .map((l) => ({
            productId: l.invoiceItem.productId,
            salesInvoiceItemId: l.invoiceItem.id,
            quantity: toNum(l.returnQty),
            itemCondition: l.condition,
            description: l.description.trim() || undefined,
          })),
      };

      const { salesReturn } = await salesReturnService.create(payload);
      navigate(`/sales/returns/${salesReturn.id}`);
    } catch (err: any) {
      setError(err.message || 'Failed to create sales return');
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingRefData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 10 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/sales/returns')} sx={{ mb: 1 }}>
        Back to Returns
      </Button>

      <PageHeader
        title="New Sales Return"
        subtitle="Raise a return against a delivered invoice, line by line, with the customer's own invoiced quantities as the cap."
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {validationErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {validationErrors.map((e) => (
            <Box key={e}>{e}</Box>
          ))}
        </Alert>
      )}

      {/* Section: Customer & Invoice */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
          Customer &amp; Invoice
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Autocomplete
              size="small"
              options={customers}
              value={customer}
              onChange={(_, v) => setCustomer(v)}
              getOptionLabel={(o) => o.company_name || ''}
              isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
              renderInput={(params) => <TextField {...params} label="Customer *" placeholder="Select customer" />}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 5 }}>
            <Autocomplete
              size="small"
              options={customerInvoices}
              value={invoice}
              onChange={(_, v) => setInvoice(v)}
              getOptionLabel={(o) =>
                `${o.invoiceNumber} · ${new Date(o.invoiceDate).toLocaleDateString('en-IN')} · ${formatIndianNumber(o.totalAmount)}`
              }
              isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
              disabled={!customer || loadingInvoices}
              loading={loadingInvoices}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Invoice *"
                  placeholder={!customer ? 'Select a customer first' : 'Select invoice'}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Return Date"
              value={returnDate}
              onChange={(e) => setReturnDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Return Type"
              value={returnType}
              onChange={(e) => setReturnType(e.target.value as ReturnType)}
            >
              {RETURN_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Warehouse (optional)"
              value={warehouseId}
              onChange={(e) => setWarehouseId(e.target.value)}
            >
              <MenuItem value="">Not specified</MenuItem>
              {warehouses.map((w) => (
                <MenuItem key={w.id} value={String(w.id)}>
                  {w.warehouseName}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              control={<Switch checked={restock} onChange={(e) => setRestock(e.target.checked)} />}
              label="Restock accepted good-condition items"
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="Reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is the customer returning these items?"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Section: Returnable Lines */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden', mb: 2.5 }}>
        <Box sx={{ p: 2.5, pb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Returnable Lines
          </Typography>
          {invoice && (
            <Typography variant="caption" color="text.secondary">
              {returnLinesCount} of {lines.length} line(s) marked for return
            </Typography>
          )}
        </Box>

        {loadingInvoiceDetail ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : !invoice ? (
          <Box sx={{ py: 5, px: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Select a customer and invoice above to load its line items.
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>Product</TableCell>
                    <TableCell sx={headerCellSx} align="right">Invoiced Qty</TableCell>
                    <TableCell sx={headerCellSx} align="right">Return Qty</TableCell>
                    <TableCell sx={headerCellSx} align="right">Unit Price</TableCell>
                    <TableCell sx={headerCellSx}>Condition</TableCell>
                    <TableCell sx={headerCellSx}>Description</TableCell>
                    <TableCell sx={headerCellSx} align="right">Line Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lines.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4, border: 0 }}>
                        <Typography variant="body2" color="text.secondary">
                          This invoice has no line items to return.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    lines.map((l) => (
                      <TableRow key={l.key} hover>
                        <TableCell sx={{ minWidth: 200 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {l.invoiceItem.productName || `Product #${l.invoiceItem.productId}`}
                          </Typography>
                          {l.invoiceItem.hsnCode && (
                            <Typography variant="caption" color="text.secondary">
                              HSN {l.invoiceItem.hsnCode}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {toNum(l.invoiceItem.quantity)} {l.invoiceItem.unit || ''}
                        </TableCell>
                        <TableCell align="right" sx={{ minWidth: 100 }}>
                          <TextField
                            size="small"
                            type="number"
                            value={l.returnQty}
                            onChange={(e) => updateLine(l.key, { returnQty: e.target.value })}
                            inputProps={{ min: 0, max: toNum(l.invoiceItem.quantity), step: '0.01', style: { textAlign: 'right' } }}
                            sx={{ minWidth: 90 }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {formatIndianNumber(toNum(l.invoiceItem.unitPrice))}
                        </TableCell>
                        <TableCell sx={{ minWidth: 130 }}>
                          <TextField
                            select
                            size="small"
                            value={l.condition}
                            onChange={(e) => updateLine(l.key, { condition: e.target.value as ItemCondition })}
                            sx={{ minWidth: 120 }}
                          >
                            {CONDITIONS.map((cond) => (
                              <MenuItem key={cond} value={cond}>
                                {cond}
                              </MenuItem>
                            ))}
                          </TextField>
                        </TableCell>
                        <TableCell sx={{ minWidth: 160 }}>
                          <TextField
                            size="small"
                            value={l.description}
                            onChange={(e) => updateLine(l.key, { description: e.target.value })}
                            placeholder="Optional note"
                            sx={{ minWidth: 150 }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ minWidth: 110 }}>
                          <Typography variant="body2" fontWeight={700}>
                            {formatIndianNumber(lineComputed(l).total)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Live totals -- informational only, server recomputes GST/totals */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2.5, borderTop: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ width: 320 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography color="text.secondary">Subtotal</Typography>
                  <Typography fontWeight={600}>{formatIndianNumber(totals.subtotal)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography color="text.secondary">Tax (GST, est.)</Typography>
                  <Typography fontWeight={600}>{formatIndianNumber(totals.totalTax)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Estimated Total
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: c.accent }}>
                    {formatIndianNumber(totals.total)}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Final GST split and totals are computed by the server on save.
                </Typography>
              </Box>
            </Box>
          </>
        )}
      </Paper>

      {/* Sticky bottom action bar */}
      <Paper
        elevation={0}
        sx={{
          position: 'sticky',
          bottom: 0,
          zIndex: 5,
          mt: 1,
          py: 1.5,
          px: 2,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          backgroundColor: c.panel,
        }}
      >
        <Button onClick={() => navigate('/sales/returns')} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="contained" startIcon={<Save />} onClick={handleSubmit} disabled={submitting}>
          {submitting ? 'Saving…' : 'Create Return'}
        </Button>
      </Paper>
    </Box>
  );
};

export default SalesReturnFormPage;
